'use client'

import { useEffect, useState, useRef } from 'react'
import { motion, useAnimation } from 'framer-motion'
import Link from 'next/link'
import { BackgroundBeams } from './_components/_ui/background-beams'

const NotFoundPage = () => {
    const [isAnimated, setIsAnimated] = useState(false);
    const [pupilPosition, setPupilPosition] = useState({ x: 0, y: 0 });
    const eyeControls = useAnimation();
    const mainEyeContainerRef = useRef(null);

    useEffect(() => {
        const sequence = async () => {
            // Initial delay before any animation starts
            await new Promise(resolve => setTimeout(resolve, 1000));

            // First, a single blink before the main animation
            await eyeControls.start({
                scaleY: [1, 0.1, 1],
                transition: { duration: 0.3, times: [0, 0.5, 1] }
            });

            // A short pause after the blink
            await new Promise(resolve => setTimeout(resolve, 200));

            // Trigger the main animation (shrinking the eye) and the text appearance
            setIsAnimated(true);
            eyeControls.start("final");
        };

        sequence();
    }, [eyeControls]);

    useEffect(() => {
        if (!isAnimated) return;

        const handleMouseMove = (event) => {
            if (!mainEyeContainerRef.current) return;
            const { clientX, clientY } = event;
            const { left, top, width, height } = mainEyeContainerRef.current.getBoundingClientRect();
            
            const eyeCenterX = left + width / 2;
            const eyeCenterY = top + height / 2;
            
            const deltaX = clientX - eyeCenterX;
            const deltaY = clientY - eyeCenterY;

            const maxPupilOffset = 10;
            
            const pupilX = (deltaX / window.innerWidth) * maxPupilOffset * 2.5;
            const pupilY = (deltaY / window.innerHeight) * maxPupilOffset * 2.5;

            setPupilPosition({
                x: Math.max(-maxPupilOffset, Math.min(maxPupilOffset, pupilX)),
                y: Math.max(-maxPupilOffset, Math.min(maxPupilOffset, pupilY)),
            });
        };

        window.addEventListener('mousemove', handleMouseMove);

        // After the main animation, start blinking randomly
        let blinkTimeout;
        const randomBlink = () => {
            eyeControls.start({
                scaleY: [1, 0.1, 1],
                transition: { duration: 0.2, times: [0, 0.5, 1] }
            });
            blinkTimeout = setTimeout(randomBlink, Math.random() * 4000 + 2000); // New blink in 2-6 seconds
        };
        blinkTimeout = setTimeout(randomBlink, Math.random() * 4000 + 2000);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            clearTimeout(blinkTimeout);
        };
    }, [isAnimated, eyeControls]);

    const eyeVariants = {
        initial: { scale: 1.2, x: 0, y: 0, opacity: 1 },
        final: { 
            scale: 0.35, 
            x: '.25rem', 
            y: '-5rem', 
            opacity: 1,
            transition: { duration: 1.5, ease: "easeInOut" }
        }
    };

    const textVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 1, delay: 1.5, ease: "easeInOut" } }
    };

    return (
        <div className="h-screen flex flex-col bg-black overflow-hidden">
            <div className="flex-1 relative flex flex-col justify-center items-center">
                <BackgroundBeams />

                <div className="relative flex flex-col items-center justify-center">
                    <motion.div
                        ref={mainEyeContainerRef}
                        className="absolute z-20"
                        style={{ originY: 0.55 }} // Setting the vertical origin for a more natural blink
                        variants={eyeVariants}
                        initial="initial"
                        animate={eyeControls}
                    >
                        <div className="w-64 h-64 rounded-full bg-white flex items-center justify-center">
                            <div className="w-32 h-32 rounded-full bg-gray-800 flex items-center justify-center">
                                <motion.div 
                                    className="w-16 h-16 rounded-full bg-black"
                                    animate={{ x: pupilPosition.x, y: pupilPosition.y }}
                                    transition={{ type: 'spring', stiffness: 120, damping: 20 }}
                                />
                            </div>
                        </div>
                    </motion.div>
                    
                    <motion.div
                        className="relative z-10 text-center"
                        variants={textVariants}
                        initial="hidden"
                        animate={isAnimated ? "visible" : "hidden"}
                    >
                        <h1 className="text-9xl font-bold text-white tracking-tighter">
                            <span className="text-white">4</span>
                            <span className="text-black">0</span>
                            <span className="text-white">4</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-300 mt-4 max-w-md mx-auto">
                            Eye don't know what you're focusing on, but this isn't the right place.
                        </p>
                        <Link href="/" className="mt-8 inline-block bg-white text-black font-semibold px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors duration-300">
                                Go Home
                        </Link>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default NotFoundPage;
